@if (loadingContent) {
  <content-loading-list></content-loading-list>
}

@if (!loadingContent) {
  <div class="profile-grid">

    <!-- Editable profile form (RF-31/RF-32) -->
    <div class="card profile-card">
      <div class="card-body">
        <h5 class="section-title">{{ 'channel.health.profile.formTitle' | translate }}</h5>
        <p class="text-muted small">{{ 'channel.health.profile.formSubtitle' | translate }}</p>

        <form [formGroup]="form" (ngSubmit)="save()">
          <div class="mb-3">
            <label class="form-label">{{ 'channel.health.profile.about' | translate }}</label>
            <input type="text" class="form-control" formControlName="about" [maxlength]="limits.about" />
            <small class="text-muted d-block text-end">{{ lengthOf('about') }} / {{ limits.about }}</small>
          </div>

          <div class="mb-3">
            <label class="form-label">{{ 'channel.health.profile.description' | translate }}</label>
            <textarea class="form-control" rows="3" formControlName="description" [maxlength]="limits.description"></textarea>
            <small class="text-muted d-block text-end">{{ lengthOf('description') }} / {{ limits.description }}</small>
          </div>

          <div class="mb-3">
            <label class="form-label">{{ 'channel.health.profile.address' | translate }}</label>
            <input type="text" class="form-control" formControlName="address" [maxlength]="limits.address" />
            <small class="text-muted d-block text-end">{{ lengthOf('address') }} / {{ limits.address }}</small>
          </div>

          <div class="mb-3">
            <label class="form-label">{{ 'channel.health.profile.email' | translate }}</label>
            <input type="email" class="form-control" formControlName="email" [maxlength]="limits.email" />
            <small class="text-muted d-block text-end">{{ lengthOf('email') }} / {{ limits.email }}</small>
          </div>

          <div class="mb-3">
            <label class="form-label">{{ 'channel.health.profile.vertical' | translate }}</label>
            <select class="form-select" formControlName="vertical">
              <option value="">—</option>
              @for (option of verticals; track option) {
                <option [value]="option">{{ ('channel.health.vertical.' + option) | translate }}</option>
              }
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">{{ 'channel.health.profile.websites' | translate }}</label>
            <input type="url" class="form-control mb-2" formControlName="website1" [maxlength]="limits.websiteLength"
              [placeholder]="'channel.health.profile.websitePlaceholder' | translate" />
            <input type="url" class="form-control" formControlName="website2" [maxlength]="limits.websiteLength"
              [placeholder]="'channel.health.profile.websitePlaceholder' | translate" />
          </div>

          <button type="submit" class="btn btn-success fw-bold" [disabled]="saving || form.invalid || form.pristine">
            @if (saving) { <i class="mdi mdi-loading mdi-spin"></i> }
            {{ 'channel.health.profile.save' | translate }}
          </button>
        </form>
      </div>
    </div>

    <!-- Live preview + photo (RF-31/RF-33) -->
    <div class="profile-side">
      <div class="card">
        <div class="card-body">
          <h5 class="section-title">{{ 'channel.health.profile.previewTitle' | translate }}</h5>
          <div class="profile-preview">
            <img class="img-thumbnail avatar-lg rounded-circle profile-preview__photo"
              [src]="photoSrc()" (error)="onImageError($event)" [alt]="channel.name" />
            <div class="profile-preview__identity">
              <strong>{{ channel.health?.signals?.verifiedName || channel.name }}</strong>
              <span>{{ channel.identifier }}</span>
              @if (form.get('about')?.value) {
                <p class="profile-preview__about">{{ form.get('about')?.value }}</p>
              }
            </div>
          </div>

          <label class="btn btn-sm btn-primary w-100 mt-3 mb-0">
            @if (uploadingPhoto) { <i class="mdi mdi-loading mdi-spin"></i> }
            {{ 'channel.health.profile.photoChange' | translate }}
            <input type="file" accept="image/*" hidden (change)="onPhotoSelected($event)" [disabled]="uploadingPhoto" />
          </label>
          <p class="text-muted small mt-1 mb-0">{{ 'channel.health.profile.photoHint' | translate }}</p>
        </div>
      </div>

      <!-- Display name (RF-34) -->
      <div class="card">
        <div class="card-body">
          <h5 class="section-title">{{ 'channel.health.profile.nameTitle' | translate }}</h5>
          <dl class="profile-facts">
            <div class="profile-facts__row">
              <dt>{{ 'channel.health.profile.nameCurrent' | translate }}</dt>
              <dd>{{ channel.health?.signals?.verifiedName || channel.name }}</dd>
            </div>
            <div class="profile-facts__row">
              <dt>{{ 'channel.health.profile.nameStatus' | translate }}</dt>
              <dd>{{ nameStatusLabel() }}</dd>
            </div>
          </dl>

          @if (hasNameReviewPending()) {
            <div class="alert alert-warning py-2 mb-0" role="alert">
              {{ 'channel.health.profile.namePending' | translate }}
            </div>
          } @else {
            <label class="form-label mt-2">{{ 'channel.health.profile.nameNew' | translate }}</label>
            <div class="profile-name-request">
              <input type="text" class="form-control" [(ngModel)]="newDisplayName"
                [ngModelOptions]="{ standalone: true }" [disabled]="requestingName" />
              <button type="button" class="btn btn-primary" (click)="requestDisplayName()"
                [disabled]="requestingName || !newDisplayName.trim()">
                @if (requestingName) { <i class="mdi mdi-loading mdi-spin"></i> }
                {{ 'channel.health.profile.nameRequest' | translate }}
              </button>
            </div>
            <p class="text-muted small mt-1 mb-0">{{ 'channel.health.profile.nameHint' | translate }}</p>
          }
        </div>
      </div>

      <!-- Read-only: verification + OBA (RF-35) -->
      <div class="card">
        <div class="card-body">
          <h5 class="section-title">{{ 'channel.health.profile.readonlyTitle' | translate }}</h5>
          <dl class="profile-facts">
            <div class="profile-facts__row">
              <dt>{{ 'channel.health.profile.verification' | translate }}</dt>
              <dd>{{ codeVerificationLabel() }}</dd>
            </div>
            <div class="profile-facts__row">
              <dt>{{ 'channel.health.profile.oba' | translate }}</dt>
              <dd>{{ obaLabel() }}</dd>
            </div>
          </dl>
          <p class="text-muted small mb-2">{{ 'channel.health.profile.obaHint' | translate }}</p>
          <a class="btn btn-sm btn-primary" href="https://business.facebook.com/wa/manage/phone-numbers/"
            target="_blank" rel="noopener">
            {{ 'channel.health.profile.obaRequest' | translate }}
            <i class="mdi mdi-open-in-new"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
}
